import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { PropsWithChildren, ReactNode } from 'react';
import { Fragment, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@sorare/core/src/atoms/ui/Popover';
import { useGreedyNav } from '@sorare/core/src/hooks/navigation/useGreedyNav';
import { useTransparentAppHeader } from '@sorare/core/src/hooks/navigation/useTransparentAppHeader';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { lazy } from '@sorare/core/src/lib/retry';
import { unitMapping } from '@sorare/core/src/lib/style';

import { SearchBarHeaderButton } from '#src/components/navigation/NewAppHeader/WebAppHeader/SearchBarHeaderButton/index.tsx';
import { TransparentMobileWebAppHeader } from '#src/components/navigation/NewAppHeader/WebAppHeader/TransparentMobileWepAppHeader/index.tsx';

const Notifications = lazy(
  async () => import('#src/components/notification/Notifications/index.tsx'),
  'Notifications'
);

const NavigationCart = lazy(
  async () => import('@sorare/marketplace/src/components/cart/NavigationCart'),
  'NavigationCart'
);

const Root = styled(Centered)`
  position: relative;
  border-bottom: 1px solid var(--c-nd-150);
  padding: var(--intermediate-unit) var(--double-unit);
`;
const PageHeaderRightButton = styled(Horizontal).attrs({ gap: 0.5 })`
  margin-left: auto;

  &:empty {
    display: none;
  }
`;
const PageHeaderTitle = styled.div`
  width: 100%;
`;

const GreedyNav = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-content: end;
  justify-items: end;
  align-items: center;
  gap: var(--unit);
  width: 100%;

  &.hasHiddenItems {
    grid-template-columns: 1fr minmax(0, max-content);
  }
`;

const Nav = styled.div<{ gap: keyof typeof unitMapping }>`
  display: inline-flex;
  min-width: max-content;
  align-items: center;
  gap: ${({ gap }) => unitMapping[gap]};
`;
const HiddenItems = styled(Vertical)`
  background: var(--c-black);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--radius-xs);
  padding: var(--unit) var(--double-unit) var(--unit) var(--unit);
`;

type Props = { extraHeaderButton?: ReactNode };
const MobileWebAppHeaderComponent = ({ extraHeaderButton }: Props) => {
  const { navRef, visibleItemsRef, itemsToHide, moreButtonRef } =
    useGreedyNav();

  const items = {
    extra: { Component: null, value: extraHeaderButton, label: null },
    cart: {
      value: null,
      Component: NavigationCart,
      label: (
        <FormattedMessage id="MobileWebAppHeader.cart" defaultMessage="Cart" />
      ),
    },
    searchBar: {
      value: null,
      Component: SearchBarHeaderButton,
      label: (
        <FormattedMessage
          id="MobileWebAppHeader.searchBar"
          defaultMessage="Search"
        />
      ),
    },
    notifications: {
      value: null,
      Component: Notifications,
      label: <FormattedMessage {...navLabels.notifications} />,
    },
  } as const;
  return (
    <Root
      left={
        <Horizontal>
          <PageHeaderTitle id="page-header-title" />
          <div id="page-header-title-breadcrumb" />
          <PageHeaderRightButton id="page-header-right-button" />
        </Horizontal>
      }
      right={
        <GreedyNav
          ref={navRef}
          className={classNames({ hasHiddenItems: !!itemsToHide })}
        >
          <Nav gap={0} ref={visibleItemsRef}>
            {Object.entries(items).map(([key, { Component, value }], index) => {
              if (itemsToHide <= index) {
                if (value) {
                  return <Fragment key={key}>{value}</Fragment>;
                }
                if (Component) {
                  return (
                    <Suspense key={key} fallback={null}>
                      <Component />
                    </Suspense>
                  );
                }
                return null;
              }
              return null;
            })}
          </Nav>
          {!!itemsToHide && (
            <PopoverRoot>
              <PopoverTrigger asChild>
                <IconButton
                  ref={moreButtonRef}
                  disableDebounce
                  icon={faEllipsisVertical}
                  size="small"
                  color="transparent"
                />
              </PopoverTrigger>
              <PopoverContent align="end">
                <HiddenItems gap={0.5}>
                  {Object.entries(items).map(
                    ([key, { Component, value, label }], index) => {
                      if (itemsToHide > index) {
                        if (value) {
                          return <Fragment key={key}>{value}</Fragment>;
                        }
                        if (Component) {
                          return (
                            <Suspense key={key} fallback={null}>
                              <Component>
                                <span className="whitespace-nowrap">
                                  {label}
                                </span>
                              </Component>
                            </Suspense>
                          );
                        }
                        return null;
                      }
                      return null;
                    }
                  )}
                </HiddenItems>
              </PopoverContent>
            </PopoverRoot>
          )}
        </GreedyNav>
      }
    >
      <div id="page-header-centered-item" />
    </Root>
  );
};

export const MobileWebAppHeader = (props: PropsWithChildren<Props>) => {
  const useTransparentHeader = useTransparentAppHeader();

  if (useTransparentHeader) {
    return <TransparentMobileWebAppHeader />;
  }
  return <MobileWebAppHeaderComponent {...props} />;
};
